import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import {
    Button,
    Input,
    ConfirmDialog,
    gtagEvent,
    formatPhone,
    stripCountryCode,
    onlyDigits,
    vars,
    firstName,
    Badge,
} from '@agendor/ui';
import { Modal } from '../components/Modal';
import { FormGroup, InputGroup, Checkbox, CheckboxLabel, CheckboxGroup, LabelRow, Divider } from '../components/Input';
import { WorkerOpeningHoursField, WorkerOpeningHoursModal } from '../components/WorkerOpeningHours';
import { PlainOpeningHours, WEEKDAYS } from '../opening-hours';
import BusinessService, {
    BusinessServiceItem,
    BusinessWorker,
    PhoneNumberAlreadyInUseError,
} from '../business-service';
import { useListModalRoute, useSingleModalRoute } from '../navigation';
import { useToast } from '../toast-context';
import { validateEmail } from '../email-validation';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    max-width: 800px;
    margin: 0 auto;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
`;

const Title = styled.h1`
    font-size: 1.5rem;
    color: ${vars.foregroundColor};
    margin: 0;
    font-weight: 600;
`;

const WorkerList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const WorkerCard = styled.div`
    background-color: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    cursor: pointer;

    &:hover {
        border-color: ${vars.foregroundColor};
        background-color: ${vars.backgroundColorBright};
    }

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
`;

const WorkerInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const WorkerName = styled.h3`
    margin: 0;
    font-size: 1.1rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const WorkerDetails = styled.div`
    display: flex;
    align-items: center;
    gap: 24px;
    flex-shrink: 0;

    @media (max-width: 600px) {
        width: 100%;
        justify-content: space-between;
    }
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;

    @media (max-width: 600px) {
        align-items: flex-start;
    }
`;

const DetailLabel = styled.span`
    font-size: 0.8rem;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
`;

const DetailValue = styled.span`
    font-size: 1.1rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
`;

const ModalWrapper = styled.div<{ hideOnDesktop?: boolean }>`
    ${(p) =>
        p.hideOnDesktop &&
        `
        @media (min-width: 601px) {
            display: none;
        }
    `}
`;

const EMPTY_SEGMENTS: string[] = [];

export default function WorkersSection({
    service,
    workers: initialWorkers,
    services,
    businessOpeningHours,
    legacyWorkersLimit,
    isActive,
    isReady,
    businessLoadError,
    onReloadBusiness,
    isLoadingBusiness,
    modalSegments,
    onNavigate,
    onReload,
    hasWhatsAppAccess,
    hasWhatsAppConfigured,
}: {
    service: BusinessService;
    workers: BusinessWorker[];
    services: BusinessServiceItem[];
    businessOpeningHours: PlainOpeningHours | null;
    // Present only on a subscription sold before workers became unlimited.
    legacyWorkersLimit?: number;
    isActive: boolean;
    isReady: boolean;
    businessLoadError: string | null;
    onReloadBusiness: () => void;
    isLoadingBusiness: boolean;
    modalSegments: string[];
    onNavigate: (segments: string[], replace?: boolean) => void;
    onReload: () => Promise<void> | void;
    hasWhatsAppAccess: boolean;
    hasWhatsAppConfigured: boolean;
}) {
    const { showToast, closeModalToast } = useToast();
    const [workers, setWorkers] = useState<BusinessWorker[]>(initialWorkers);
    const [routeReady, setRouteReady] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        confirmText: string;
        onConfirm: () => void;
    }>({ isOpen: false, title: '', message: '', confirmText: '', onConfirm: () => {} });
    const [editingWorker, setEditingWorker] = useState<(Omit<BusinessWorker, 'id'> & { id?: number }) | null>(null);

    const findById = useCallback((id: string) => workers.find((w) => String(w.id) === id), [workers]);

    const {
        state: modalState,
        error: modalRouteError,
        openNew,
        openItem,
        close,
    } = useListModalRoute<BusinessWorker>({
        isActive,
        ready: routeReady,
        segments: modalSegments,
        onNavigate,
        findById,
        label: 'profissionais',
        singularLabel: 'profissional',
        baseSegments: EMPTY_SEGMENTS,
    });

    // Nested one level in, so the grid is its own address and closing it lands back on
    // the professional instead of on the list.
    const openWorkerId = modalState.kind === 'item' ? modalState.id : null;
    const hoursBaseSegments = React.useMemo(
        () => (openWorkerId === null ? EMPTY_SEGMENTS : [openWorkerId]),
        [openWorkerId],
    );
    const {
        isOpen: isHoursModalOpen,
        open: openHoursModal,
        close: closeHoursModal,
    } = useSingleModalRoute({
        isActive: openWorkerId !== null,
        ready: businessOpeningHours !== null,
        segments: modalSegments.slice(1),
        onNavigate,
        modalKey: 'opening-hours',
        baseSegments: hoursBaseSegments,
    });

    useEffect(() => {
        setWorkers(initialWorkers);
    }, [initialWorkers]);

    useEffect(() => {
        const hasData = workers.length > 0 || modalSegments.length === 0 || modalSegments[0] === 'new';
        setRouteReady(isReady && hasData);
    }, [workers, isReady, modalSegments]);

    useEffect(() => {
        if (modalState.kind === 'none') {
            setEditingWorker(null);
            closeModalToast();
            return;
        }
        if (modalState.kind === 'new') {
            setEditingWorker({
                id: undefined,
                name: '',
                title: '',
                servicesIds: [],
                email: null,
                phoneNumber: null,
                calendarUrl: '',
                isActive: true,
                openingHours: null,
            });
            return;
        }
        // Format phone number for display when editing
        const item = modalState.item;
        setEditingWorker({
            ...item,
            phoneNumber: item.phoneNumber ? formatPhone(stripCountryCode(item.phoneNumber)) : null,
            isActive: item.isActive,
        });
    }, [modalState, closeModalToast]);

    const handleDeleteClick = () => {
        if (!editingWorker?.id) return;
        gtagEvent('workers_delete_click');

        const workerToDelete = editingWorker;

        setConfirmDialog({
            isOpen: true,
            title: 'Remover Profissional',
            message: 'Tem certeza que deseja remover este profissional? Esta ação não pode ser desfeita.',
            confirmText: 'Remover',
            onConfirm: async () => {
                try {
                    setIsSaving(true);
                    await service.deleteWorker({ workerId: workerToDelete.id! });
                    showToast('Profissional removido com sucesso!', 'success', 'app');
                    setConfirmDialog({ isOpen: false, title: '', message: '', confirmText: '', onConfirm: () => {} });
                    close();
                    onReload();
                } catch (error) {
                    console.error('Failed to delete worker', error);
                    showToast('Não foi possível remover o profissional.', 'error', 'app');
                } finally {
                    setIsSaving(false);
                }
            },
        });
    };

    // The cap counts active workers only.
    const assertCapAllowsOneMoreActive = (): boolean => {
        if (legacyWorkersLimit === undefined) return true;
        if (workers.filter((w) => w.isActive).length < legacyWorkersLimit) return true;
        showToast(
            `Seu plano atual permite até ${legacyWorkersLimit} ${legacyWorkersLimit === 1 ? 'profissional ativo' : 'profissionais ativos'}. Para adicionar mais, altere o plano.`,
            'error',
            'modal',
        );
        return false;
    };

    const handleSave = async () => {
        if (!editingWorker) return;
        gtagEvent('workers_save_click');
        if (!editingWorker.name) {
            showToast('Nome é obrigatório.', 'error', 'modal');
            return;
        }
        if (!editingWorker.title) {
            showToast('Função é obrigatória.', 'error', 'modal');
            return;
        }

        const emailValidation = validateEmail(editingWorker.email);
        if (!emailValidation.isValid) {
            showToast(emailValidation.errorMessage, 'error', 'modal');
            return;
        }
        if (modalState.kind === 'new' && !assertCapAllowsOneMoreActive()) return;

        setIsSaving(true);
        try {
            if (modalState.kind === 'new') {
                await service.createWorker({
                    name: editingWorker.name,
                    title: editingWorker.title,
                    servicesIds: editingWorker.servicesIds ?? [],
                });
                showToast('Profissional criado com sucesso!', 'success', 'app');
            } else if (modalState.kind === 'item') {
                const workerId = Number(modalState.id);

                let phoneToSend: string | null = null;
                if (editingWorker.phoneNumber !== null) {
                    const rawPhone = onlyDigits(editingWorker.phoneNumber);
                    phoneToSend = rawPhone.length >= 10 ? '55' + rawPhone : null;
                }

                await service.updateWorker({
                    workerId,
                    name: editingWorker.name,
                    title: editingWorker.title,
                    servicesIds: editingWorker.servicesIds ?? [],
                    phoneNumber: phoneToSend,
                    email: emailValidation.trimmedEmail,
                });

                showToast('Profissional atualizado com sucesso!', 'success', 'app');
            }
            close();
            onReload();
        } catch (error) {
            console.error('Failed to save worker', error);
            if (error instanceof PhoneNumberAlreadyInUseError) {
                showToast('Este número de WhatsApp já está em uso.', 'error', 'modal');
            } else {
                showToast('Não foi possível salvar o profissional.', 'error', 'modal');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleInputChange = (field: keyof BusinessWorker, value: string | null) => {
        if (!editingWorker) return;
        if (field === 'phoneNumber' && value !== null) {
            setEditingWorker((prev) => (prev ? { ...prev, [field]: formatPhone(value) } : prev));
        } else {
            setEditingWorker((prev) => (prev ? { ...prev, [field]: value } : prev));
        }
    };

    const toggleWorkerCalendarEmail = (enabled: boolean) => {
        if (!editingWorker) return;
        setEditingWorker((prev) =>
            prev
                ? {
                      ...prev,
                      email: enabled ? (prev.email ?? '') : null,
                  }
                : prev,
        );
    };

    const toggleWorkerPhone = (enabled: boolean) => {
        if (!editingWorker) return;
        setEditingWorker((prev) =>
            prev
                ? {
                      ...prev,
                      phoneNumber: enabled ? (prev.phoneNumber ?? '') : null,
                  }
                : prev,
        );
    };

    const handleServiceToggle = (serviceId: number) => {
        if (!editingWorker) return;
        setEditingWorker((prev) => {
            if (!prev) return prev;
            const currentServices = prev.servicesIds || [];
            return currentServices.includes(serviceId)
                ? { ...prev, servicesIds: currentServices.filter((id) => id !== serviceId) }
                : { ...prev, servicesIds: [...currentServices, serviceId] };
        });
    };

    const handleToggleActive = async () => {
        if (!editingWorker?.id) return;
        gtagEvent('workers_toggle_active_click', { action: !editingWorker.isActive ? 'active' : 'inactive' });

        const newIsActive = !editingWorker.isActive;
        if (newIsActive && !assertCapAllowsOneMoreActive()) return;

        // Deactivating requires confirmation
        if (!newIsActive) {
            setConfirmDialog({
                isOpen: true,
                title: 'Desativar Profissional',
                message:
                    'Profissionais desativados não aparecem para clientes e não podem receber agendamentos. Deseja continuar?',
                confirmText: 'Desativar',
                onConfirm: () => performToggleActive(editingWorker.id!, false),
            });
            return;
        }

        await performToggleActive(editingWorker.id, true);
    };

    const applySavedHours = (workerId: number, openingHours: PlainOpeningHours | null) => {
        setEditingWorker((prev) => (prev ? { ...prev, openingHours } : prev));
        setWorkers((prev) => prev.map((w) => (w.id === workerId ? { ...w, openingHours } : w)));
    };

    // The setting persists on toggle rather than on the form's Salvar, the way open scope does.
    const handleToggleCustomHours = async (enabled: boolean) => {
        if (!editingWorker?.id) return;
        const workerId = editingWorker.id;
        gtagEvent('workers_opening_hours_toggle_click', { action: enabled ? 'on' : 'off', item_id: workerId });

        setIsSaving(true);
        try {
            await service.setWorkerOpeningHoursEnabled({ workerId, enabled });
            await onReload();
        } catch (error) {
            console.error('Failed to save worker opening hours setting', error);
            showToast('Não foi possível salvar a configuração.', 'error', 'modal');
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenHoursModal = () => {
        if (!editingWorker?.id) return;
        gtagEvent('workers_opening_hours_click', { item_id: editingWorker.id });
        openHoursModal();
    };

    const handleCloseHoursModal = () => {
        if (editingWorker?.id) gtagEvent('workers_opening_hours_cancel_click', { item_id: editingWorker.id });
        closeHoursModal();
    };

    const handleCopyBusinessHours = () => {
        if (!editingWorker?.id) return;
        gtagEvent('workers_opening_hours_copy_click', { item_id: editingWorker.id });
    };

    const handleSaveHours = async (openingHours: PlainOpeningHours) => {
        if (!editingWorker?.id) return;
        const workerId = editingWorker.id;
        const workerFirstName = firstName(editingWorker.name);
        gtagEvent('workers_opening_hours_save_click', {
            item_id: workerId,
            value: WEEKDAYS.filter((weekday) => openingHours[weekday].length > 0).length,
        });

        setIsSaving(true);
        try {
            await service.setWorkerOpeningHours({ workerId, openingHours });
            applySavedHours(workerId, openingHours);
            closeHoursModal();
            showToast(`Horário de ${workerFirstName} salvo com sucesso!`, 'success', 'modal');
            onReload();
        } catch (error) {
            console.error('Failed to save worker opening hours', error);
            showToast('Não foi possível salvar o horário.', 'error', 'modal');
        } finally {
            setIsSaving(false);
        }
    };

    const performToggleActive = async (workerId: number, newIsActive: boolean) => {
        setIsSaving(true);
        try {
            await service.setWorkerActive({ workerId, isActive: newIsActive });
            setEditingWorker((prev) => (prev ? { ...prev, isActive: newIsActive } : prev));
            setWorkers((prev) => prev.map((w) => (w.id === workerId ? { ...w, isActive: newIsActive } : w)));
            setConfirmDialog({ isOpen: false, title: '', message: '', confirmText: '', onConfirm: () => {} });
            showToast(newIsActive ? 'Profissional ativado!' : 'Profissional desativado.', 'success', 'modal');
            onReload();
        } catch (error) {
            console.error('Failed to toggle worker active status', error);
            showToast('Não foi possível alterar o status do profissional.', 'error', 'modal');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Container>
            <Header>
                <Title>Profissionais</Title>
                {modalRouteError && (
                    <div
                        style={{
                            padding: '8px 10px',
                            borderRadius: 6,
                            border: `1px solid ${vars.dangerColorDark}`,
                            background: vars.dangerTintSoft,
                            color: vars.foregroundColor,
                        }}
                    >
                        {modalRouteError}
                    </div>
                )}
                <Button
                    primary
                    data-testid="new-worker-btn"
                    onClick={() => {
                        gtagEvent('workers_new_click');
                        openNew();
                    }}
                >
                    Novo Profissional
                </Button>
            </Header>

            {businessLoadError ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    <div style={{ marginBottom: '12px', color: vars.foregroundColor }}>{businessLoadError}</div>
                    <Button onClick={onReloadBusiness} disabled={isLoadingBusiness}>
                        {isLoadingBusiness ? 'Carregando...' : 'Tentar novamente'}
                    </Button>
                </div>
            ) : !isReady ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Carregando profissionais...</div>
            ) : (
                <WorkerList>
                    {workers.length > 0 ? (
                        workers.map((worker) => (
                            <WorkerCard
                                key={worker.id}
                                onClick={() => {
                                    gtagEvent('workers_item_click', { item_id: worker.id });
                                    openItem(String(worker.id));
                                }}
                            >
                                <WorkerInfo>
                                    <WorkerName>
                                        {worker.name}
                                        {!worker.isActive && (
                                            <Badge tone="muted" data-testid="badge-inactive">
                                                Desativado
                                            </Badge>
                                        )}
                                    </WorkerName>
                                    <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '4px' }}>
                                        {worker.servicesIds.length} serviços habilitados
                                    </div>
                                </WorkerInfo>

                                <WorkerDetails>
                                    <DetailItem>
                                        <DetailLabel>Função</DetailLabel>
                                        <DetailValue>{worker.title}</DetailValue>
                                    </DetailItem>
                                    {hasWhatsAppAccess && (
                                        <DetailItem>
                                            <DetailLabel>WhatsApp</DetailLabel>
                                            <DetailValue>{worker.phoneNumber || '-'}</DetailValue>
                                        </DetailItem>
                                    )}
                                </WorkerDetails>
                            </WorkerCard>
                        ))
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                            Nenhum profissional cadastrado.
                        </div>
                    )}
                </WorkerList>
            )}

            {editingWorker && (
                <ModalWrapper hideOnDesktop={confirmDialog.isOpen}>
                    <Modal
                        isOpen
                        onClose={close}
                        data-testid="worker-modal"
                        title={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {editingWorker.id ? 'Editar Profissional' : 'Novo Profissional'}
                                {editingWorker.id && !editingWorker.isActive && <Badge tone="muted">Desativado</Badge>}
                            </span>
                        }
                        footer={
                            <>
                                <Button
                                    data-testid="modal-cancel"
                                    onClick={() => {
                                        gtagEvent('workers_cancel_click');
                                        close();
                                    }}
                                    disabled={isSaving}
                                >
                                    Cancelar
                                </Button>
                                {editingWorker.id && (
                                    <Button
                                        data-testid="modal-delete"
                                        danger
                                        onClick={handleDeleteClick}
                                        disabled={isSaving}
                                    >
                                        Remover
                                    </Button>
                                )}
                                <Button
                                    data-testid="modal-save"
                                    primary
                                    type="submit"
                                    form="worker-form"
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Salvando...' : 'Salvar'}
                                </Button>
                            </>
                        }
                    >
                        <form
                            id="worker-form"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSave();
                            }}
                        >
                            <FormGroup>
                                <InputGroup>
                                    Nome
                                    <Input
                                        type="text"
                                        value={editingWorker.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        placeholder="Ex.: João Silva"
                                        disabled={isSaving}
                                    />
                                </InputGroup>
                                <InputGroup>
                                    Função
                                    <Input
                                        type="text"
                                        value={editingWorker.title}
                                        onChange={(e) => handleInputChange('title', e.target.value)}
                                        placeholder="Ex.: Barbeiro"
                                        disabled={isSaving}
                                    />
                                </InputGroup>
                                <InputGroup>
                                    Serviços
                                    {services.length > 0 ? (
                                        <CheckboxGroup style={{ padding: 0, maxHeight: '200px', overflowY: 'auto' }}>
                                            {services.map((service) => (
                                                <CheckboxLabel key={service.id}>
                                                    <Checkbox
                                                        checked={
                                                            editingWorker.servicesIds?.includes(service.id) || false
                                                        }
                                                        onChange={() => handleServiceToggle(service.id)}
                                                    />
                                                    {service.name}
                                                </CheckboxLabel>
                                            ))}
                                        </CheckboxGroup>
                                    ) : (
                                        <div style={{ fontSize: '0.85rem', color: '#666', fontWeight: 400 }}>
                                            Nenhum serviço cadastrado
                                        </div>
                                    )}
                                </InputGroup>
                                {editingWorker.id && businessOpeningHours !== null && (
                                    <>
                                        <Divider />
                                        <InputGroup>
                                            <LabelRow>Horário de trabalho</LabelRow>
                                            <WorkerOpeningHoursField
                                                openingHours={editingWorker.openingHours}
                                                onToggle={handleToggleCustomHours}
                                                onConfigure={handleOpenHoursModal}
                                                disabled={isSaving}
                                            />
                                        </InputGroup>
                                    </>
                                )}
                                {editingWorker.id && (
                                    <>
                                        {hasWhatsAppAccess && (
                                            <>
                                                <Divider />
                                                <InputGroup>
                                                    <CheckboxLabel>
                                                        <Checkbox
                                                            checked={editingWorker.phoneNumber !== null}
                                                            onChange={(e) => toggleWorkerPhone(e.target.checked)}
                                                        />
                                                        Receber notificações pelo WhatsApp
                                                    </CheckboxLabel>
                                                    {editingWorker.phoneNumber !== null && (
                                                        <>
                                                            <LabelRow>WhatsApp</LabelRow>
                                                            <Input
                                                                type="tel"
                                                                value={editingWorker.phoneNumber || ''}
                                                                onChange={(e) =>
                                                                    handleInputChange('phoneNumber', e.target.value)
                                                                }
                                                                placeholder="(00) 00000-0000"
                                                                maxLength={15}
                                                            />
                                                            {!hasWhatsAppConfigured && (
                                                                <div
                                                                    style={{
                                                                        marginTop: '8px',
                                                                        padding: '8px 12px',
                                                                        fontSize: '0.85rem',
                                                                        color: '#333',
                                                                        backgroundColor: '#fff3cd',
                                                                        border: '1px solid #ffeeba',
                                                                        borderRadius: '4px',
                                                                    }}
                                                                >
                                                                    As notificações só serão enviadas após conectar o
                                                                    WhatsApp na aba Assistente.
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </InputGroup>
                                            </>
                                        )}
                                        <Divider />
                                        <InputGroup>
                                            <LabelRow>Google Agenda</LabelRow>
                                            {editingWorker.calendarUrl && (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        margin: '0 0 10px 0',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                    }}
                                                >
                                                    <Button
                                                        as="a"
                                                        href={editingWorker.calendarUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        style={{ alignSelf: 'flex-start' }}
                                                        onClick={() => gtagEvent('workers_calendar_open_click')}
                                                    >
                                                        Abrir agenda
                                                    </Button>
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                <CheckboxLabel>
                                                    <Checkbox
                                                        checked={editingWorker.email !== null}
                                                        onChange={(e) => toggleWorkerCalendarEmail(e.target.checked)}
                                                    />
                                                    Acesso à própria agenda
                                                </CheckboxLabel>
                                                {editingWorker.email !== null && (
                                                    <Input
                                                        type="email"
                                                        value={editingWorker.email || ''}
                                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                                        placeholder="Email com acesso à agenda do profissional"
                                                    />
                                                )}
                                            </div>
                                        </InputGroup>
                                        <div
                                            style={{
                                                borderTop: `1px solid ${vars.dividerColor}`,
                                                margin: '16px 0 8px',
                                            }}
                                        />
                                        <Button
                                            type="button"
                                            onClick={handleToggleActive}
                                            disabled={isSaving}
                                            style={{ alignSelf: 'flex-start' }}
                                            data-testid="toggle-worker-active-btn"
                                        >
                                            {editingWorker.isActive ? 'Desativar profissional' : 'Ativar profissional'}
                                        </Button>
                                    </>
                                )}
                            </FormGroup>
                        </form>
                    </Modal>
                </ModalWrapper>
            )}

            {editingWorker?.id && businessOpeningHours !== null && (
                <WorkerOpeningHoursModal
                    isOpen={isHoursModalOpen}
                    title={`Horário de ${firstName(editingWorker.name)}`}
                    workerOpeningHours={editingWorker.openingHours}
                    businessOpeningHours={businessOpeningHours}
                    isSaving={isSaving}
                    onClose={handleCloseHoursModal}
                    onCopyBusinessHours={handleCopyBusinessHours}
                    onSave={handleSaveHours}
                />
            )}

            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.title}
                message={confirmDialog.message}
                confirmText={confirmDialog.confirmText}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() =>
                    setConfirmDialog({ isOpen: false, title: '', message: '', confirmText: '', onConfirm: () => {} })
                }
                isLoading={isSaving}
            />
        </Container>
    );
}
